/**
 * Exact arithmetic for financial ratios. Every KPI is computed as an exact
 * fraction of integers (BigInt), never with floating point, and rounded once,
 * at the very end, by the single rule below.
 *
 * ROUNDING RULE (the only one in the system): round half away from zero.
 *   2.345 → 2.35, −2.345 → −2.35, 2.344 → 2.34.
 * Display precision by kind: money to the cent; percentages to 0.1 point;
 * ratios to 0.01; days and months to 0.1.
 */

export class Exact {
  readonly n: bigint;
  readonly d: bigint;

  private constructor(n: bigint, d: bigint) {
    if (d === 0n) throw new RangeError("Division by zero");
    if (d < 0n) {
      n = -n;
      d = -d;
    }
    const g = gcd(n < 0n ? -n : n, d);
    this.n = n / g;
    this.d = d / g;
  }

  /** From whole cents (or any integer). Refuses anything that isn't a safe integer. */
  static int(v: number | bigint): Exact {
    if (typeof v === "number" && !Number.isSafeInteger(v)) throw new RangeError(`Not a safe integer: ${v}`);
    return new Exact(BigInt(v), 1n);
  }

  static frac(n: number | bigint, d: number | bigint): Exact {
    return Exact.int(n).div(Exact.int(d));
  }

  /** Reads back what toString() wrote: "123" or "-41/7". */
  static parse(s: string): Exact {
    const m = /^(-?\d{1,60})(?:\/(\d{1,60}))?$/.exec(s);
    if (!m) throw new RangeError(`Not an exact value: "${s.slice(0, 40)}"`);
    return new Exact(BigInt(m[1]!), m[2] ? BigInt(m[2]) : 1n);
  }

  /** From decimal text such as "0.40" or "-12.5" (no floating point involved). */
  static decimal(s: string): Exact {
    const m = /^(-)?(\d{1,30})(?:\.(\d{1,12}))?$/.exec(s.trim());
    if (!m) throw new RangeError(`Not a decimal: "${s.slice(0, 40)}"`);
    const frac = m[3] ?? "";
    const n = BigInt(`${m[2]}${frac}`) * (m[1] ? -1n : 1n);
    return new Exact(n, 10n ** BigInt(frac.length));
  }

  abs(): Exact {
    return this.n < 0n ? this.neg() : this;
  }

  add(o: Exact): Exact {
    return new Exact(this.n * o.d + o.n * this.d, this.d * o.d);
  }
  sub(o: Exact): Exact {
    return new Exact(this.n * o.d - o.n * this.d, this.d * o.d);
  }
  mul(o: Exact): Exact {
    return new Exact(this.n * o.n, this.d * o.d);
  }
  div(o: Exact): Exact {
    if (o.n === 0n) throw new RangeError("Division by zero");
    return new Exact(this.n * o.d, this.d * o.n);
  }
  neg(): Exact {
    return new Exact(-this.n, this.d);
  }
  isZero(): boolean {
    return this.n === 0n;
  }
  sign(): -1 | 0 | 1 {
    return this.n === 0n ? 0 : this.n < 0n ? -1 : 1;
  }
  cmp(o: Exact): -1 | 0 | 1 {
    return this.sub(o).sign();
  }
  eq(o: Exact): boolean {
    return this.n === o.n && this.d === o.d;
  }

  /** Rounded to `places` decimals (half away from zero), as a scaled integer: 12.35 at 2 places → 1235n. */
  scaled(places: number): bigint {
    const scale = 10n ** BigInt(places);
    const num = this.n * scale;
    const neg = num < 0n;
    const a = neg ? -num : num;
    const q = a / this.d;
    const r = a % this.d;
    const rounded = r * 2n >= this.d ? q + 1n : q;
    return neg ? -rounded : rounded;
  }

  /** Rounded decimal string, e.g. "12.35", "-0.10", "3". */
  toFixed(places: number): string {
    const s = this.scaled(places);
    const neg = s < 0n;
    const digits = (neg ? -s : s).toString().padStart(places + 1, "0");
    const body = places ? `${digits.slice(0, -places)}.${digits.slice(-places)}` : digits;
    return neg && s !== 0n ? `-${body}` : body;
  }

  /** Whole cents, rounded by the rule (for money results such as EBITDA or break-even revenue). */
  toCents(): number {
    const c = this.scaled(0);
    if (c > BigInt(Number.MAX_SAFE_INTEGER) || c < BigInt(Number.MIN_SAFE_INTEGER)) throw new RangeError("Amount too large");
    return Number(c);
  }

  toString(): string {
    return this.d === 1n ? this.n.toString() : `${this.n}/${this.d}`;
  }
}

function gcd(a: bigint, b: bigint): bigint {
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}

/**
 * The system's rounding rule for a plain number: to the nearest whole, halves away from zero
 * (2.5 → 3, −2.5 → −3, 2.4999 → 2). Use it wherever an amount is rounded to whole cents or dollars;
 * `Math.round` sends −2.5 to −2. A number a hair under a half because of floating point (such as
 * 1.0049999 × 100) is not nudged: compute such figures with `Exact` and round once.
 */
export function roundHalfAwayFromZero(n: number): number {
  if (!Number.isFinite(n)) throw new RangeError(`Not a finite number: ${n}`);
  const r = Math.round(Math.abs(n));
  return n < 0 ? (r === 0 ? 0 : -r) : r;
}
